/** Beneficiary (Contact) EDIT form — the explicit-Save counterpart to the
 * read-only ContactDetailPage.
 *
 * The whole field set (identity + address + legal-entity + custom fields) is
 * the shared <BeneficiaryFormFields>, so this page and both create modals
 * stay in lockstep. This page owns the load/save + layout. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BeneficiaryFormFields } from "@/components/BeneficiaryFormFields";
import {
  beneficiaryFormFromContact,
  beneficiaryPayload,
  canSubmitBeneficiary,
  emptyBeneficiaryForm,
  type BeneficiaryForm,
} from "@/components/beneficiaryForm";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError } from "@/lib/apiErrors";
import { useHasPermission } from "@/lib/permissions";
import type { CampaignMembership, Contact } from "@/lib/types";
import { ContactAddresses, MembershipFieldsCard } from "@/modules/campaigns";

export default function ContactEditPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canManageMembers = useHasPermission("campaign.members.manage");

  const contact = useQuery({
    queryKey: ["contact", id],
    queryFn: async () => {
      const res = await api.get<Contact>(ApiPaths.contact(id!));
      return res.data;
    },
    enabled: !!id,
  });

  // Backlog 2026-09-18 — this form only ever covered CONTACT-scoped
  // custom fields; a beneficiary's per-CAMPAIGN (membership-scoped)
  // fields had no edit surface here at all (only on the read-only
  // detail page's per-membership card, and only for someone who found
  // their way there instead of to "Edit"). Same query + same card as
  // ContactDetailPage, just rendered on the edit page too.
  const memberships = useQuery({
    queryKey: ["contact-memberships", id],
    queryFn: async () => {
      const res = await api.get<CampaignMembership[]>(
        ApiPaths.contactMemberships(id!),
      );
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!id,
  });

  const [form, setForm] = useState<BeneficiaryForm>(emptyBeneficiaryForm);
  const [customFields, setCustomFields] = useState<Record<string, string>>({});

  // Seed the form once the contact loads.
  useEffect(() => {
    const c = contact.data;
    if (!c) return;
    setForm(beneficiaryFormFromContact(c));
    setCustomFields(
      Object.fromEntries(
        Object.entries(c.custom_fields_jsonb || {}).map(([k, v]) => [
          k,
          String(v ?? ""),
        ]),
      ),
    );
  }, [contact.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.patch<Contact>(
        ApiPaths.contact(id!),
        // Addresses are managed by the structured <ContactAddresses> manager
        // below (the source of truth); don't write flat address columns here.
        beneficiaryPayload(form, customFields, { includeAddress: false }),
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(["contact", id], data);
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      navigate(`/contacts/${id}`);
    },
  });

  const canSubmit = canSubmitBeneficiary(form);

  if (contact.isLoading) {
    return (
      <AppLayout title={t("contacts.editTitle", "Edit beneficiary")}>
        <p className="card text-sm text-nb-text-3">{t("common.loading")}</p>
      </AppLayout>
    );
  }
  if (!contact.data) {
    return (
      <AppLayout title={t("contacts.editTitle", "Edit beneficiary")}>
        <p className="card text-sm text-cvp-danger">{t("contacts.notFound")}</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout
      eyebrow={t("contacts.eyebrow")}
      title={t("contacts.editTitle", "Edit beneficiary")}
    >
      <button
        type="button"
        className="text-sm text-nb-text-2 hover:underline"
        onClick={() => navigate(`/contacts/${id}`)}
      >
        ← {t("common.cancel")}
      </button>

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit && !save.isPending) save.mutate();
        }}
      >
        <BeneficiaryFormFields
          form={form}
          onChange={(patch) => setForm((f) => ({ ...f, ...patch }))}
          customFields={customFields}
          onCustomFieldChange={(key, value) =>
            setCustomFields((d) => ({ ...d, [key]: value }))
          }
          hideAddress
        />

        {save.isError && (
          <p className="text-sm text-cvp-danger">
            {describeApiError(save.error, t("contacts.createErrorGeneric"))}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => navigate(`/contacts/${id}`)}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={!canSubmit || save.isPending}
          >
            {save.isPending ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </form>

      {/* Structured, country-aware, multi-address manager — the source of
          truth for this contact's addresses (same manager as the detail
          page). Rendered OUTSIDE the identity form (it has its own <form>)
          and saves immediately on its own; the flat columns are kept in sync
          by the backend structured→flat mirror. */}
      <ContactAddresses contactId={id!} isBusiness={form.is_business} />

      {/* Per-campaign (membership-scoped) custom fields — self-contained,
          saves immediately per campaign, same card ContactDetailPage
          uses. Rendered only when the beneficiary has memberships and
          at least one has fields to show/edit. */}
      {memberships.data && memberships.data.length > 0 && (
        <section className="card space-y-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
            {t("contacts.membershipsHeading")}
          </h2>
          <ul className="divide-y divide-nb-border">
            {memberships.data.map((m) => (
              <li key={m.id} className="py-2 text-sm">
                <span className="font-medium text-nb-text">
                  {m.campaign_name ||
                    `${t("contacts.campaignPrefix")} ${m.campaign.slice(0, 8)}`}
                </span>
                <MembershipFieldsCard
                  campaign={m.campaign}
                  membershipId={m.id}
                  values={m.custom_fields_jsonb}
                  canEdit={canManageMembers}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </AppLayout>
  );
}
